import { t } from '@transifex/native';

import { isApp } from 'pkg/platform';

/**
 *	A poll on a chat message (PLA-580), as the API serves it. The API tallies
 *	it for the viewer: each option's `count`, the distinct `voterCount` the
 *	bars are drawn against and the viewer's own `myOptionIds`. When `anonymous`
 *	is set every option's `votes` is empty for everyone.
 */
export interface Poll {
	id: number;
	question: string;
	multipleChoice: boolean;
	anonymous: boolean;
	/** How many people have voted; a multiple-choice voter counts once. */
	voterCount: number;
	createdByUserId: number;
	options: PollOption[];
	/**
	 *	The viewer's picks. `null` in a `chat.message.updated` push, which
	 *	every member receives; the client keeps its own then.
	 */
	myOptionIds: number[] | null;
	/**
	 *	The poll's version: every vote raises it, and the tally that comes
	 *	with it is the state at that version. Pushes can arrive out of order,
	 *	so a tally older than the one on screen is dropped.
	 */
	updatedAt: number;
}

export interface PollOption {
	id: number;
	label: string;
	sortOrder: number;
	count: number;
	votes: PollVote[];
}

/** Who voted and when. The client names them from the chat's users. */
export interface PollVote {
	userId: number;
	createdAt: number;
}

/** What creating a poll takes; the API trims and validates it. */
export interface CreatePayload {
	question: string;
	options: string[];
	multipleChoice: boolean;
	anonymous: boolean;
}

/** `incoming` is a tally from before the one in `current`. */
export function isOlder(
	incoming: Poll | null | undefined,
	current: Poll | null | undefined
): boolean {
	return !!incoming && !!current && incoming.updatedAt < current.updatedAt;
}

/**
 *	A poll as legacy chat shows it, which can't draw one. In the app an
 *	update brings the new chat; on the web there is nothing to update.
 */
export function pollFallback(): string {
	return isApp()
		? t('📊 Poll · update the app to vote')
		: t('📊 Poll · not available in this chat view');
}

export const MinOptions: number = 2;
export const MaxOptions: number = 12;
export const MaxQuestionLength: number = 500;
export const MaxOptionLength: number = 200;

/** The input a poll was created from, for sending it again. */
export function payloadOf(poll: Poll): CreatePayload {
	return {
		question: poll.question,
		options: poll.options.map((option) => option.label),
		multipleChoice: poll.multipleChoice,
		anonymous: poll.anonymous,
	};
}

/**
 *	The poll as the thread shows it while the create is on its way: the
 *	options under negative ids no real option has, nobody has voted.
 */
export function temporaryPoll(
	payload: CreatePayload,
	createdByUserId: number
): Poll {
	return {
		id: 0,
		question: payload.question,
		multipleChoice: payload.multipleChoice,
		anonymous: payload.anonymous,
		voterCount: 0,
		createdByUserId,
		myOptionIds: [],
		updatedAt: 0,
		options: payload.options.map(
			(label, index): PollOption => ({
				id: -(index + 1),
				label,
				sortOrder: index,
				count: 0,
				votes: [],
			})
		),
	};
}
